import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Hourglass, CheckCircle2, Clock, XCircle, AlertCircle } from "lucide-react";
import { Button, Spinner } from "@/components/earnos/kit";
import { apiFetch } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { colors } from "@/lib/theme";

export default function BillingReturn() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [params] = useSearchParams();
  const [state, setState] = useState(params.get("cancelled") ? "cancelled" : "checking");
  const [plan, setPlan] = useState(null);
  const tries = useRef(0);
  const sessionId = params.get("session_id");

  useEffect(() => {
    if (state !== "checking") return;
    if (!sessionId) { setState("error"); return; }
    let active = true;
    let timer;
    tries.current = 0;
    const poll = async () => {
      if (!active) return;
      try {
        const res = await apiFetch(`/billing/status/${encodeURIComponent(sessionId)}`);
        if (!active) return;
        if (res.payment_status === "paid") { setPlan(res.kind === "subscription" ? res.plan : null); setState("paid"); await refresh(); return; }
        if (res.status === "expired") { setState("error"); return; }
      } catch { /* ignore */ }
      if (!active) return;
      tries.current += 1;
      if (tries.current >= 10) { if (active) setState("pending"); return; }
      timer = setTimeout(poll, 1500);
    };
    poll();
    return () => { active = false; clearTimeout(timer); };
  }, [state, sessionId, refresh]);

  const cfg = {
    checking: { Icon: Hourglass, color: colors.brandSecondary, title: "Confirming your payment…", msg: "This only takes a moment." },
    paid: { Icon: CheckCircle2, color: colors.success, title: "Payment confirmed", msg: plan ? `You're now on the ${plan.toUpperCase()} plan.` : "Thank you — your payment was received." },
    pending: { Icon: Clock, color: colors.warning, title: "Still processing", msg: "Your payment is taking a little longer. It will update automatically once confirmed." },
    cancelled: { Icon: XCircle, color: colors.onSurfaceSecondary, title: "Checkout cancelled", msg: "No charge was made. You can pick a plan whenever you're ready." },
    error: { Icon: AlertCircle, color: colors.error, title: "We couldn't confirm this payment", msg: "If you were charged, it will still be applied. Check your payment history." },
  }[state];

  const Icon = cfg.Icon;

  return (
    <div className="min-h-screen bg-surface flex flex-col items-center justify-center px-6 text-center">
      {state === "checking" ? <Spinner /> : (
        <div className="w-22 h-22 w-[88px] h-[88px] rounded-full flex items-center justify-center" style={{ backgroundColor: cfg.color + "22" }}><Icon size={44} style={{ color: cfg.color }} /></div>
      )}
      <h1 className="text-2xl font-extrabold text-ink mt-6">{cfg.title}</h1>
      <p className="text-lg text-ink-2 mt-3 max-w-md leading-relaxed">{cfg.msg}</p>
      {state !== "checking" ? <Button title="Back to billing" onClick={() => navigate("/billing", { replace: true })} className="mt-6 min-w-[220px]" testID="return-back" /> : null}
    </div>
  );
}
